import { useEffect, useState } from "react";
import styled from "styled-components";
import { SelectOptionType } from "./MultiSelect";
import { COLOR_PRIMARY } from "../../constants/color"; // Make sure to import core colors

type Props = {
    isOpen: boolean;
    uuid: string;
    options: SelectOptionType[];
    width: string;
    selectedValues: any[];
    handleChange: (value: any) => void;
    hasSearchInput?: boolean;
};

export const MultiSelectDropdown: React.FC<Props> = ({
    isOpen,
    uuid,
    options,
    width,
    selectedValues,
    handleChange,
    hasSearchInput = true,
}) => {
    const [optionsToShow, setOptionsToShow] = useState<SelectOptionType[]>(options);

    useEffect(() => {
        setOptionsToShow(options);
    }, [options]);

    return (
        <StyledDropdownContainer $width={width} $isOpen={isOpen}>
            <StyledOptionContainer id={`select-options-${uuid}`}>
                {optionsToShow.length === 0 && (
                    <StyledNoOptionMessage>No data</StyledNoOptionMessage>
                )}

                {optionsToShow.map((option, i) => {
                    const isSelected = selectedValues.some(val => String(val) === String(option.value));

                    return (
                        <StyledDropdownOption
                            key={`${option.id}-${i}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleChange(option.value);
                            }}
                        >
                            <StyledCheckbox $checked={isSelected}>
                                {isSelected && <StyledCheckIcon viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></StyledCheckIcon>}
                            </StyledCheckbox>
                            <StyledOptionText>{option.name}</StyledOptionText>
                        </StyledDropdownOption>
                    );
                })}
            </StyledOptionContainer>
        </StyledDropdownContainer>
    );
};

const StyledDropdownContainer = styled.div<{
    $width: string;
    $isOpen: boolean;
}>`
  @media (max-width: 768px) {
    width: 100%;
  }

  position: absolute;
  top: 100%;
  width: ${({ $width }) => $width};
  max-height: ${({ $isOpen }) => ($isOpen ? "328px" : "0")};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.24);
  transition: max-height 0.2s ease-in-out;
  z-index: 100;
`;

const StyledOptionContainer = styled.div`
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 15px 16px;
  outline: none;
`;

const StyledNoOptionMessage = styled.div`
  padding: 16px;
  color: #cccccc;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`;

const StyledDropdownOption = styled.div`
  padding: 8px 0px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const StyledCheckbox = styled.div<{ $checked: boolean }>`
    width: 18px;
    height: 18px;
    border: 1px solid ${({ $checked }) => $checked ? COLOR_PRIMARY : '#CCCCCC'};
    background-color: ${({ $checked }) => $checked ? COLOR_PRIMARY : 'transparent'};
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    transition: all 0.2s;
`;

const StyledCheckIcon = styled.svg`
    width: 12px;
    height: 12px;
    fill: none;
    stroke: white;
    stroke-width: 3px;
    stroke-linecap: round;
    stroke-linejoin: round;
`;

const StyledOptionText = styled.div`
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  white-space: normal;
  overflow: hidden;
  word-wrap: break-word;
`;
